/**
 * TokenSearch — lets the user add any token by:
 *   • Typing a symbol name (searches CoinGecko)
 *   • Pasting a Solana contract address (looks up via Birdeye)
 *   • Pasting an EVM contract address (looks up via CoinGecko contracts)
 */

import { useState, useRef, useCallback } from 'react';
import type { Token } from '@arc/shared';
import { resolveContractAddress } from '@arc/shared';
import { useTerminalStore } from '../../stores/terminal.store.js';

type SearchResult = Token & { isExact?: boolean };

async function lookupToken(query: string): Promise<SearchResult[]> {
  const resolved = resolveContractAddress(query);

  if (resolved) {
    // Contract address — single lookup
    const res = await fetch(`/api/tokens/lookup?address=${encodeURIComponent(query)}`, {
      signal: AbortSignal.timeout(10_000),
    });
    if (!res.ok) throw new Error(`Lookup failed: ${res.status}`);
    const data = await res.json() as { token?: Token; error?: string };
    if (data.error) throw new Error(data.error);
    return data.token ? [{ ...data.token, isExact: true }] : [];
  }

  // Symbol / name search
  const res = await fetch(`/api/tokens/lookup?q=${encodeURIComponent(query)}`, {
    signal: AbortSignal.timeout(10_000),
  });
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  const data = await res.json() as { tokens?: Token[] };
  return (data.tokens ?? []).map(t => ({ ...t, isExact: false }));
}

export function TokenSearch() {
  const [query,    setQuery]    = useState('');
  const [results,  setResults]  = useState<SearchResult[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [open,     setOpen]     = useState(false);

  const { addToken, addToWatchlist, tokens } = useTerminalStore();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) { setResults([]); setOpen(false); return; }

    setLoading(true);
    setError(null);

    try {
      const found = await lookupToken(trimmed);
      setResults(found);
      setOpen(found.length > 0);
    } catch (err) {
      setError((err as Error).message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 500);
  };

  const handleAdd = (token: SearchResult) => {
    const exists = tokens.find(t => t.symbol === token.symbol);
    if (exists) {
      addToWatchlist(token.symbol);
    } else {
      addToken(token);
    }
    setQuery('');
    setResults([]);
    setOpen(false);
    setError(null);
  };

  const chainBadge = (token: Token) => {
    if (!token.chain) return 'CG';
    const map: Record<string, string> = {
      solana: 'SOL', ethereum: 'ETH', bsc: 'BSC', arbitrum: 'ARB', base: 'BASE',
    };
    return map[token.chain] ?? token.chain.toUpperCase();
  };

  return (
    <div className="relative">
      {/* Input container with clip-path parallelogram */}
      <div
        className="flex items-center gap-1 transition-all duration-200"
        style={{
          background: 'rgba(0,212,255,0.05)',
          border: '1px solid var(--color-border)',
          clipPath: 'polygon(4px 0%, 100% 0%, calc(100% - 4px) 100%, 0% 100%)',
        }}
      >
        <span className="pl-2 text-[11px] opacity-90" style={{ color: 'var(--color-arc)' }}>⌕</span>
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={e => {
            e.currentTarget.closest('div')!.style.borderColor = 'var(--color-arc)';
            e.currentTarget.closest('div')!.style.background = 'rgba(0,212,255,0.08)';
            results.length > 0 && setOpen(true);
          }}
          onBlur={e => {
            e.currentTarget.closest('div')!.style.borderColor = 'var(--color-border)';
            e.currentTarget.closest('div')!.style.background = 'rgba(0,212,255,0.05)';
            setTimeout(() => setOpen(false), 200);
          }}
          placeholder="Symbol or contract address…"
          className="flex-1 bg-transparent font-mono text-[11px] py-1.5 px-1 outline-none"
          style={{ color: 'var(--color-arc)' }}
        />
        {loading && (
          <span className="pr-2 text-[11px] animate-pulse" style={{ color: 'var(--color-arc)' }}>SCAN</span>
        )}
      </div>

      {error && (
        <div className="font-mono text-[11px] mt-1 px-1" style={{ color: 'var(--color-sell)' }}>
          {error}
        </div>
      )}

      {/* Dropdown results */}
      {open && results.length > 0 && (
        <div
          className="absolute left-0 right-0 z-50 shadow-xl max-h-48 overflow-y-auto"
          style={{
            border: '1px solid var(--color-arc)',
            background: '#040d1a',
          }}
        >
          {results.map((token, i) => (
            <button
              key={`${token.symbol}-${i}`}
              onMouseDown={() => handleAdd(token)}
              className="w-full text-left px-2 py-1.5 flex items-center gap-2 group transition-colors"
              style={{ borderBottom: '1px solid var(--color-border)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,212,255,0.08)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span className="font-orb font-bold text-[11px] tracking-wider" style={{ color: 'var(--color-bright)' }}>{token.symbol}</span>
              <span className="font-mono text-[11px] flex-1 truncate" style={{ color: 'var(--color-muted)' }}>{token.name}</span>
              <span
                className="font-mono text-[11px] px-1"
                style={{
                  color: 'var(--color-arc)',
                  border: '1px solid rgba(0,212,255,0.3)',
                  clipPath: 'polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%)',
                }}
              >
                {chainBadge(token)}
              </span>
              <span className="font-mono text-[11px] opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--color-arc)' }}>+ ADD</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
